/** Presentational renderer for the committed chip list. */
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Chip } from '@mui/material';
import type { MouseEvent } from 'react';

import { condDisplay, displayValue, type CondChip } from './chipLabel';
import { chipToString, type ChipToken } from './chips';
import type { NamespaceConfig } from './types';

function condLabel(chip: CondChip, cfg: NamespaceConfig | undefined) {
  const isId = chip.operator === 'idmatch';
  const shown = condDisplay(chip, cfg);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.25, minWidth: 0 }}>
      {/* any/all scope: an all-scoped booking-level condition (the `*`
          modifier) carries a compact leading badge so the modifier is visible
          on the committed chip, not just while typing. */}
      {chip.scope === 'all' && (
        <Box
          component="span"
          title="Every booking in the order must match (all)"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            color: 'primary.main',
            lineHeight: 1,
            alignSelf: 'center',
          }}
        >
          ∀
        </Box>
      )}
      <Box component="b">{chip.namespace}</Box>
      {shown.sigil !== null ? (
        <Box
          component="span"
          title={isId ? 'Matches the selected item by its ID' : undefined}
          sx={{ color: isId ? 'primary.main' : 'text.disabled', fontWeight: isId ? 700 : undefined }}
        >
          {shown.sigil}
        </Box>
      ) : (
        <Box component="span" sx={{ width: 4 }} />
      )}
      {/* A live window moves with every refresh: the icon marks it, and its words
          ("last 15 minutes", "since …") say so too. */}
      {shown.live && (
        <UpdateIcon aria-hidden sx={{ fontSize: 14, color: 'primary.main', alignSelf: 'center', mr: 0.25 }} />
      )}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {shown.text}
      </Box>
    </Box>
  );
}

const STRUCT_LABEL: Record<'and' | 'or' | 'not' | 'lparen' | 'rparen', string> = {
  and: '&',
  or: '|',
  not: '!',
  lparen: '(',
  rparen: ')',
};

interface Props {
  chips: ChipToken[];
  /** Namespace configs — a datetime condition reads as words ("last 15 minutes"). */
  namespaces?: Record<string, NamespaceConfig>;
  /** Delete the chip at `index`. */
  onDelete: (index: number) => void;
  /** Index of the chip currently focused for structural editing (navigate/
   *  delete/negate/swap via keyboard), if any. */
  focusedIndex?: number;
  /** Click a condition or free-text chip's body (not its delete icon) to edit it in
   *  place (ChipEditor, anchored to the chip). */
  onEditChip?: (index: number, anchor: HTMLElement) => void;
}

export function TokenChips({ chips, namespaces, onDelete, focusedIndex, onEditChip }: Props) {
  // The container's own click opens the namespace dropdown — an edit must not.
  const edit = (e: MouseEvent<HTMLElement>, index: number) => {
    e.stopPropagation();
    onEditChip?.(index, e.currentTarget);
  };
  return (
    <>
      {chips.map((chip, i) => {
        const isFocused = i === focusedIndex;
        const focusSx = isFocused
          ? { outline: (t: { palette: { primary: { main: string } } }) => `2px solid ${t.palette.primary.main}`, outlineOffset: '1px' }
          : undefined;
        if (chip.t === 'cond') {
          const cfg = namespaces?.[chip.namespace];
          return (
            <Chip
              key={i}
              size="small"
              variant="outlined"
              color={isFocused ? 'primary' : 'default'}
              label={condLabel(chip, cfg)}
              // The query text behind the words, for hover and copy-by-eye.
              title={chipToString(chip)}
              data-chip-index={i}
              aria-haspopup={onEditChip ? 'dialog' : undefined}
              onDelete={() => onDelete(i)}
              onClick={onEditChip ? (e) => edit(e, i) : undefined}
              sx={{ maxWidth: 260, ...focusSx }}
            />
          );
        }
        if (chip.t === 'word') {
          return (
            <Chip
              key={i}
              size="small"
              color={isFocused ? 'primary' : 'default'}
              label={displayValue(chip.text)}
              data-chip-index={i}
              aria-haspopup={onEditChip ? 'dialog' : undefined}
              onDelete={() => onDelete(i)}
              onClick={onEditChip ? (e) => edit(e, i) : undefined}
              sx={focusSx}
            />
          );
        }
        return (
          <Box
            key={i}
            component="span"
            role="button"
            tabIndex={-1}
            onClick={() => onDelete(i)}
            sx={{
              color: 'text.disabled',
              fontFamily: 'monospace',
              px: 0.5,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: 14,
              borderRadius: 0.5,
              ...(isFocused && { outline: (t) => `1px solid ${t.palette.primary.main}` }),
            }}
          >
            {STRUCT_LABEL[chip.t]}
          </Box>
        );
      })}
    </>
  );
}
